<template>
  <div class="map" ref="map"></div>
</template>

<script>
import googleMapsLoader from '@/mixins/googleMapsLoader'

import { mapState } from 'vuex'

import { toRaw } from 'vue'

import InfoWindowView from './InfoWindowView.vue'

import { createApp } from 'vue'

export default {
  data() {
    return {
      center: this.mapCenter ? this.mapCenter : { lat: 38.01663433250827, lng: -98.16988275885976 },
      zoom: this.mapZoom ? this.mapZoom : 5,
      options: {
        mapTypeControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        styles: null,
      },
      markers: [],
      icon: {
        url: '',
        scaledSize: { width: 37, height: 50 },
      },
      userIcon: {
        url: '',
        scaledSize: { width: 37, height: 50 },
      },
      map: null,
    }
  },
  props: ['selectedService', 'mapCenter', 'mapZoom', 'mapMarkers', 'fitBounds'],
  mixins: [googleMapsLoader],
  watch: {
    branding(newVal, oldVal) {
      if (oldVal == null && newVal) {
        this.options.styles = this.branding.map_style
        this.icon.url = this.branding.markers.store_marker
        this.userIcon.url = this.branding.markers.user_marker
      }
    },
  },
  computed: {
    ...mapState(['branding','storeConfig']),
  },
  methods: {
    createMap() {
      this.loader
        .load()
        .then(async () => {
          const { Map } = await window.google.maps.importLibrary('maps')
          this.map = new Map(this.$refs.map, {
            center: this.center,
            zoom: this.zoom,
            ...this.options,
          })
        })
        .catch((error) => {
          console.log(error)
        })
    },
    createMarkers(stores, selectedAddressLat, selectedAddressLng) {
      try {
        this.removeMarkers()
        if (selectedAddressLat && selectedAddressLng) {
          this.addUserMarker(selectedAddressLat, selectedAddressLng)
        }
        if (this.mapMarkers) {
          this.mapMarkers.forEach((marker) => {
            // if marker.position.lat and marker.position.lng are null then skip this marker
            if (!marker.position.lat || !marker.position.lng) {
              return
            }
            let confirmationMarker = new window.google.maps.Marker({
              position: {
                lat: marker.position.lat,
                lng: marker.position.lng,
              },
              icon: marker.icon,
              map: this.map,
              hasInfoWindow: false,
            })
            this.markers.push(confirmationMarker)
          })
        }
        if (stores) {
          stores.forEach((store) => {
            let marker = new window.google.maps.Marker({
              position: {
                lat: store.latitude,
                lng: store.longitude,
              },
              icon: {
                url: store.extra?.icon || this.icon.url,
                scaledSize: { width: 37, height: 50 },
              },
              map: this.map,
              store: store,
              hasInfoWindow: true,
            })
            this.markers.push(marker)
          })
        }
        this.addMarkersToMap()
      } catch (error) {
        console.log(error)
      }
    },
    addMarkersToMap() {
      this.markers.forEach((marker) => {
        marker.setMap(this.map)
      })
      if (this.fitBounds && this.markers.length > 1) {
        this.adjustMapZoom()
      } else {
        this.map.setCenter(this.markers[0].position)
        this.map.setZoom(14)
      }
      this.createMapInfoWindow()
    },
    removeMarkers() {
      // remove all markers from map
      this.markers.forEach((marker) => {
        toRaw(marker).setMap(null)
      })
      // clear markers array
      this.markers = []
    },
    addUserMarker(selectedAddressLat, selectedAddressLng) {
      let marker = new window.google.maps.Marker({
        position: {
          lat: selectedAddressLat,
          lng: selectedAddressLng,
        },
        icon: this.userIcon,
      })
      this.markers.push(marker)
      // now add markers to map
      marker.setMap(this.map)
      if (this.fitBounds) {
        this.adjustMapZoom()
      }
    },
    adjustMapZoom() {
      const bounds = new window.google.maps.LatLngBounds()
      this.markers.forEach((marker) => {
        bounds.extend(marker.position)
      })
      this.map.fitBounds(bounds)
    },
    createMapInfoWindow() {
      this.markers.forEach((marker) => {
        if (marker.hasInfoWindow) {
          marker.infoWindow = new window.google.maps.InfoWindow({
            content: document.createElement('div'),
          })
          marker.marker = marker
          marker.addListener('click', () => {
            this.openInfoWindow(marker)
          })
        }
      })
    },
    openInfoWindow(marker, storeId) {
      if (storeId) {
        marker = this.markers.find((marker) => {
          return marker.store.store_id === storeId
        })
      }
      this.$emit('clickedStoreId', marker.store.store_id)
      // change the map center to the marker position and zoom in
      this.map.setCenter(marker.position)
      this.map.setZoom(10)
      // close all other info windows
      this.markers.forEach((marker) => {
        if (marker.infoWindow) {
          marker.infoWindow.close()
        }
      })
      const app = createApp(InfoWindowView, {
        store: marker.store,
        selectedService: this.selectedService,
        darkMode: this.branding?.dark_mode_enabled,
        showAddress: this.branding.display_options?.hide_store_address,
        showPhone: this.branding.display_options?.hide_restaurant_phone_number,
        onSelectStore: (store, from) => {
          this.$emit('selectStore', store, from)
        },
      })
      const componentInstance = app.mount(document.createElement('div'))
      marker.infoWindow.setContent(componentInstance.$el)
      marker.infoWindow.open(this.map, marker.marker)
    },
  },
  created() {
    this.options.styles = this.branding.map_style
    this.icon.url = this.branding.markers.store_marker
    this.userIcon.url = this.branding.markers.user_marker
  },
  mounted() {
    this.createMap()
  },
}
</script>

<style scoped></style>
